import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import PageSeo from "@/components/PageSeo";
import TrustedBy from "@/components/sections/TrustedBy";

/**
 * City landing pages for GEO / local SEO.  First city: Bengaluru.
 *
 * Route:  /production/bangalore
 * Schema: LocalBusiness + Service + FAQPage (all three carry ranking weight
 *         for "brand film production near me / in bangalore" queries).
 *
 * Content is intentionally template-driven, not LLM-generated: the studio
 * is real, the numbers are real, and the copy is written for the actual
 * search intent ("production house bangalore", "corporate video bangalore",
 * "brand film company bangalore").  Additional cities (Mumbai, Delhi, etc.)
 * can plug into the same component by extending CITY_DATA — kept in-file
 * for now while there's only one live city.
 */
const CITY_DATA = {
  bangalore: {
    slug: "bangalore",
    display: "Bengaluru",
    displayAlt: "Bangalore",
    tagline: "Films that scale with your ambition, made from the city we call home.",
    intro:
      "Relatable Studio is a Bengaluru-based production house. Our studio and client-meeting space is at BHIVE Platinum on Church Street — a 12-minute drive from Cubbon Park, 25 from the airport at Sadahalli — and we've shot in more meeting rooms, factories, campuses and living rooms across this city than we can count.",
    stats: [
      { k: "1,600+", v: "projects delivered" },
      { k: "300+",   v: "brands served" },
      { k: "40+",    v: "Bengaluru-based teams" },
      { k: "Since 2020", v: "in Bengaluru" },
    ],
    services: [
      {
        title: "Brand Films",
        href: "/services/brand-film-production-bangalore",
        desc: "Story-first films for founders, brand-launches, and category-defining moments.",
        deliverables: ["3-8 min hero film", "Social cuts (60s / 30s / 15s)", "Behind-the-scenes"],
      },
      {
        title: "Employer Branding Films",
        href: "/services/employer-branding-films-bangalore",
        desc: "Real teams, real work, no stock footage. Films that fill your pipeline.",
        deliverables: ["Culture film", "Recruiter cuts", "Career page hero"],
      },
      {
        title: "Product & Explainer Films",
        href: "/services/product-video-production-bangalore",
        desc: "Cutdowns for SaaS, D2C, and B2B products that translate feature-lists into feelings.",
        deliverables: ["60s explainer", "Feature cut-downs", "Demo film"],
      },
      {
        title: "Founder & Leadership Storytelling",
        href: "/services/startup-video-production-bangalore",
        desc: "Interview-led films that make founders sound like the humans they are.",
        deliverables: ["Founder film", "Leadership series", "Podcast video"],
      },
      {
        title: "Event Films & Documentaries",
        href: "/services/corporate-film-production-bangalore",
        desc: "Multi-cam coverage for conferences, launches, and long-form documentaries.",
        deliverables: ["Wrap film", "Speaker cuts", "Documentary edit"],
      },
    ],
    whyHere: [
      {
        n: "01",
        t: "We know the crews.",
        d: "Bengaluru has more freelance DPs, sound recordists and production designers per square kilometer than any other Indian city. We work with the ones who actually pick up on Sunday.",
      },
      {
        n: "02",
        t: "We know the locations.",
        d: "From Whitefield glass towers to Malleshwaram basement studios, from BEL 4th Stage to Nandi Hills. We shortlist locations that fit the brief before we scout.",
      },
      {
        n: "03",
        t: "We know the permit chain.",
        d: "BBMP, Karnataka Film Chamber, private-society NOCs, forest clearances. What takes three weeks for a first-timer takes us a phone call.",
      },
      {
        n: "04",
        t: "We know the weather.",
        d: "Bengaluru shoots need a mid-day break in the monsoon and a 5am callsheet in December. We build production schedules around the sky, not against it.",
      },
    ],
    faqs: [
      {
        q: "How much does a brand film cost in Bengaluru?",
        a: "A one-day, one-location interview-led brand film starts at ₹4-6 lakhs for a full production (crew, camera, edit, delivery). A multi-day, multi-location hero brand film with a director-DP combo, colour, sound design and social cutdowns typically lands between ₹12 and ₹35 lakhs. We publish a full pricing PDF on request — send a note through /contact and we'll share it.",
      },
      {
        q: "Do you shoot outside Bengaluru?",
        a: "Yes. Bengaluru is where our office and edit rooms are, but ~40% of our shoots happen elsewhere in India — Mumbai, Delhi-NCR, Hyderabad, Chennai, and dozens of smaller cities and rural locations. Travel + local crew is quoted separately and transparently.",
      },
      {
        q: "How long does a brand film production take?",
        a: "For a standard interview-led film, count 4-6 weeks from kickoff to master delivery: 1 week of pre-production, 2-3 days of shoot, 2-3 weeks of edit, 1 week of colour + sound + revisions. Complex campaign films with treatment-led directors can run 8-12 weeks.",
      },
      {
        q: "Can you handle scripting, or do we need a separate agency?",
        a: "Both. About 60% of our projects come with a brief and expect us to script and direct. The other 40% arrive with a locked agency script. We're comfortable either way — the only thing we won't do is shoot a bad script without flagging it first.",
      },
      {
        q: "Do you own your kit or hire?",
        a: "We own our A-cams (Sony FX3, FX6, and a Canon C70), lenses (a Sigma cine set + Zeiss primes), lights, and audio. Anything above that — Alexa, RED, drones, cranes, gimbals — is hired per project from our regular Bengaluru rental partners. This keeps our overheads low so more of your budget goes on-screen.",
      },
      {
        q: "Are you a good fit for a very small budget?",
        a: "Sometimes. If your budget is under ₹3 lakhs, we'll be honest about what we can and can't deliver. We'd rather turn a project down than deliver something we're not proud of. That said, we do have a lean-crew 'founder film' package we can pitch — write to us.",
      },
    ],
    address: {
      street: "BHIVE Platinum, Church Street Campus",
      locality: "Bengaluru",
      region: "Karnataka",
      postal: "560001",
      country: "IN",
      lat: 12.9755,
      lng: 77.6058,
    },
  },
};

function buildLocalBusinessLd(city) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": ["LocalBusiness", "ProfessionalService"],
        "@id": `https://relatable.studio/production/${city.slug}#business`,
        "name": `Relatable Studio · ${city.display}`,
        "url": `https://relatable.studio/production/${city.slug}`,
        "image": "https://customer-assets.emergentagent.com/job_brand-cinema-8/artifacts/3j3f8ta6_RelatableLogo-White.png",
        "telephone": "+91-93800-21206",
        "email": "nexa@relatable.studio",
        "priceRange": "₹₹₹",
        "areaServed": [
          { "@type": "City", "name": city.display },
          { "@type": "AdministrativeArea", "name": city.address.region },
          { "@type": "Country", "name": "India" },
        ],
        "address": {
          "@type": "PostalAddress",
          "streetAddress": city.address.street,
          "addressLocality": city.address.locality,
          "addressRegion": city.address.region,
          "postalCode": city.address.postal,
          "addressCountry": city.address.country,
        },
        "geo": { "@type": "GeoCoordinates", "latitude": city.address.lat, "longitude": city.address.lng },
        "makesOffer": city.services.map((s) => ({
          "@type": "Offer",
          "itemOffered": { "@type": "Service", "name": s.title, "description": s.desc },
        })),
      },
      {
        "@type": "FAQPage",
        "@id": `https://relatable.studio/production/${city.slug}#faq`,
        "mainEntity": city.faqs.map((f) => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
      },
    ],
  };
}

export default function ProductionCity({ citySlug = "bangalore" }) {
  const city = CITY_DATA[citySlug];
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/projects?featured=true").then((r) => {
      setProjects((r.data || []).slice(0, 6));
    }).catch(() => {});
  }, []);

  if (!city) return null;

  return (
    <main className="bg-ink text-bone">
      <PageSeo
        title={`Brand Film Production in ${city.display} · Relatable Studio`}
        description={`Bengaluru-based production house building brand films, employer branding films, founder stories, and product explainers. 1,600+ projects · 300+ brands · Church Street office. Get a quote for your ${city.display} shoot.`}
        path={`/production/${city.slug}`}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Production", path: `/production/${city.slug}` },
          { name: city.display, path: `/production/${city.slug}` },
        ]}
        jsonLd={buildLocalBusinessLd(city)}
      />

      {/* HERO */}
      <section
        data-testid="city-hero"
        className="relative pt-32 md:pt-40 pb-16 md:pb-24 px-6 md:px-12 lg:px-24 border-b border-line"
      >
        <p className="eyebrow text-fog text-xs md:text-sm tracking-[0.28em]">
          ◇ production · {city.display.toLowerCase()}, karnataka
        </p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mt-6 font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter font-medium leading-[0.95] max-w-5xl"
        >
          Brand film production
          <br />
          <span className="text-fog/60">in </span>
          <span className="text-relatable">{city.display}</span>.
        </motion.h1>
        <p className="mt-8 md:mt-10 font-display text-xl md:text-2xl text-bone/75 leading-snug max-w-3xl">
          {city.tagline}
        </p>
        <p className="mt-6 text-base md:text-lg text-fog/85 leading-relaxed max-w-3xl">
          {city.intro}
        </p>

        {/* Stat strip */}
        <div className="mt-12 md:mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 max-w-4xl">
          {city.stats.map((s) => (
            <div key={s.v} data-testid={`city-stat-${s.v.replace(/\s+/g, "-").toLowerCase()}`}>
              <p className="font-display text-3xl md:text-5xl tracking-tight font-medium">{s.k}</p>
              <p className="mt-2 eyebrow text-fog text-[10px] md:text-xs tracking-[0.22em]">{s.v}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 flex flex-wrap items-center gap-4">
          <Link
            to={`/contact?utm_source=production-${city.slug}&utm_medium=hero-cta&utm_campaign=city-landing`}
            data-testid="city-hero-cta"
            className="group inline-flex items-center gap-3 bg-bone text-ink px-6 py-3 md:px-8 md:py-4 font-mono text-sm tracking-wider hover:bg-relatable hover:text-bone transition-colors"
          >
            Get a quote for {city.display}
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
          <a
            href="tel:+919380021206"
            data-testid="city-hero-call"
            className="inline-flex items-center gap-3 border border-line text-bone px-6 py-3 md:px-8 md:py-4 font-mono text-sm tracking-wider hover:border-bone transition-colors"
          >
            Call the studio · +91 93800 21206
          </a>
        </div>
      </section>

      {/* SERVICES WE OFFER HERE */}
      <section
        data-testid="city-services"
        className="py-20 md:py-28 px-6 md:px-12 lg:px-24 border-b border-line"
      >
        <p className="eyebrow text-fog text-xs md:text-sm tracking-[0.28em]">◇ what we build here</p>
        <h2 className="mt-6 font-display text-4xl md:text-6xl tracking-tighter font-medium leading-[0.98] max-w-4xl">
          Five kinds of films <br className="hidden md:block"/>
          <span className="text-fog/60">we keep shooting in </span>{city.display}.
        </h2>
        <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14">
          {city.services.map((s, i) => (
            <div key={s.title} data-testid={`city-service-${i}`} className="border-t border-line pt-6">
              <p className="font-mono text-xs text-fog tracking-[0.22em]">
                0{i + 1} / 0{city.services.length}
              </p>
              <h3 className="mt-4 font-display text-2xl md:text-3xl tracking-tight font-medium">
                {s.href ? (
                  <Link to={s.href} className="hover:text-relatable transition-colors" data-testid={`city-service-link-${i}`}>
                    {s.title} <ArrowUpRight className="inline w-5 h-5 opacity-60" />
                  </Link>
                ) : (
                  s.title
                )}
              </h3>
              <p className="mt-3 text-base text-bone/75 leading-relaxed max-w-lg">
                {s.desc}
              </p>
              <ul className="mt-5 space-y-1.5">
                {s.deliverables.map((d) => (
                  <li key={d} className="font-mono text-xs text-fog/85 tracking-wider">
                    → {d}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* WHY BENGALURU */}
      <section
        data-testid="city-why"
        className="py-20 md:py-28 px-6 md:px-12 lg:px-24 border-b border-line bg-graphite/50"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          <div className="md:col-span-1">
            <p className="eyebrow text-fog text-xs tracking-[0.28em]">◇ why hire local</p>
            <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tighter font-medium leading-[0.98]">
              Four things a{" "}
              <span className="text-relatable">{city.display} crew</span>
              {" "}saves you.
            </h2>
          </div>
          <div className="md:col-span-2 space-y-8">
            {city.whyHere.map((w) => (
              <div key={w.n} data-testid={`city-why-${w.n}`} className="flex gap-6 border-t border-line pt-6">
                <span className="font-mono text-xs text-fog tracking-[0.28em] shrink-0">{w.n}</span>
                <div>
                  <h3 className="font-display text-xl md:text-2xl tracking-tight font-medium">{w.t}</h3>
                  <p className="mt-2 text-base text-bone/75 leading-relaxed max-w-xl">{w.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUSTED BY (reuse) */}
      <TrustedBy />

      {/* FEATURED WORK */}
      {projects.length > 0 && (
        <section
          data-testid="city-work"
          className="py-20 md:py-28 px-6 md:px-12 lg:px-24 border-b border-line"
        >
          <div className="flex items-end justify-between gap-6 mb-10 md:mb-14">
            <div>
              <p className="eyebrow text-fog text-xs md:text-sm tracking-[0.28em]">◇ recent work</p>
              <h2 className="mt-6 font-display text-4xl md:text-6xl tracking-tighter font-medium leading-[0.98] max-w-3xl">
                Made in {city.display}.<br/>
                <span className="text-fog/60">Shipped everywhere.</span>
              </h2>
            </div>
            <Link
              to="/work"
              className="hidden md:inline-flex items-center gap-2 eyebrow text-bone link-line whitespace-nowrap pb-2"
            >
              All work <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {projects.map((p) => (
              <Link
                key={p.slug || p.id}
                to={`/work/${p.slug}`}
                data-testid={`city-work-${p.slug}`}
                className="group block"
              >
                <div className="aspect-[4/3] bg-graphite overflow-hidden">
                  {p.thumbnail_url && (
                    <img
                      src={p.thumbnail_url}
                      alt={p.title || "Project"}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  )}
                </div>
                <p className="mt-4 font-display text-lg tracking-tight font-medium group-hover:text-relatable transition-colors">
                  {p.title}
                </p>
                <p className="mt-1 eyebrow text-fog text-[10px] tracking-[0.22em]">
                  {p.category || p.division}
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* FAQ — schema-backed for AEO */}
      <section
        data-testid="city-faq"
        className="py-20 md:py-28 px-6 md:px-12 lg:px-24 border-b border-line"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          <div className="md:col-span-1">
            <p className="eyebrow text-fog text-xs tracking-[0.28em]">◇ everyone asks</p>
            <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tighter font-medium leading-[0.98]">
              Straight answers,<br/>
              <span className="text-fog/60">before the call.</span>
            </h2>
          </div>
          <div className="md:col-span-2 space-y-8">
            {city.faqs.map((f, i) => (
              <details
                key={f.q}
                data-testid={`city-faq-${i}`}
                className="group border-t border-line pt-6 open:pb-2"
              >
                <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                  <span className="font-display text-lg md:text-2xl tracking-tight font-medium leading-snug">
                    {f.q}
                  </span>
                  <span className="font-mono text-xs text-fog/70 tracking-[0.22em] shrink-0 pt-2 group-open:rotate-45 transition-transform">
                    +
                  </span>
                </summary>
                <p className="mt-4 text-base text-bone/75 leading-relaxed max-w-2xl">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA — end-of-page lead capture */}
      <section
        data-testid="city-cta"
        className="py-24 md:py-32 px-6 md:px-12 lg:px-24"
      >
        <div className="max-w-4xl">
          <p className="eyebrow text-fog text-xs md:text-sm tracking-[0.28em]">◇ next step</p>
          <h2 className="mt-6 font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter font-medium leading-[0.95]">
            Ready to make<br/>
            <span className="text-relatable">something in {city.display}?</span>
          </h2>
          <p className="mt-8 font-display text-xl md:text-2xl text-bone/75 max-w-2xl">
            Tell us what you&apos;re trying to say. We&apos;ll come back within one working day with a treatment direction and a ballpark budget.
          </p>
          <div className="mt-12 flex flex-wrap items-center gap-4">
            <Link
              to={`/contact?utm_source=production-${city.slug}&utm_medium=footer-cta&utm_campaign=city-landing`}
              data-testid="city-footer-cta"
              className="group inline-flex items-center gap-3 bg-bone text-ink px-6 py-3 md:px-8 md:py-4 font-mono text-sm tracking-wider hover:bg-relatable hover:text-bone transition-colors"
            >
              Brief the studio
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <a
              href="mailto:nexa@relatable.studio?subject=Bengaluru%20project"
              data-testid="city-footer-email"
              className="inline-flex items-center gap-3 border border-line text-bone px-6 py-3 md:px-8 md:py-4 font-mono text-sm tracking-wider hover:border-bone transition-colors"
            >
              nexa@relatable.studio
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
